/**
 * <TutorWeekEditor> — l'éditeur de la semaine-type, sans chrome d'écran (organism).
 *
 * Legacy `MaDispoEditor` : les deux parties numérotées — ① la date de démarrage, ② la semaine — puis
 * ce qu'elle fait au total. Une seule expérience de la disponibilité, où qu'on la règle : la page
 * « Ma dispo » l'enveloppe de son bandeau et de son bouton, l'étape « Ma dispo » du profil
 * (`TutorAvailabilityStep`) la pose telle quelle et laisse le formulaire porter les valeurs.
 *
 * ① ne porte pas d'avertissement : le calendrier, borné à [aujourd'hui, aujourd'hui + 1 an] comme
 * le serveur, s'ouvre là où le tuteur peut agir (`openOn`, aujourd'hui quand la date posée est
 * passée), et le changement de date se confirme dans la boîte de la page.
 *
 * Contrôlé : la date et la semaine arrivent en props, chaque changement remonte. Le calendrier
 * ouvert est un état d'interface, il vit dans `<DatePicker>`.
 */
import React, { useMemo } from "react"

import { Stack } from "../../atoms/containers/Stack"
import { DatePicker } from "../../molecules/DatePicker"
import { TutorStepHeading } from "../../molecules/TutorStepHeading"
import { TutorWeekSummary } from "../../molecules/TutorWeekSummary"
import { TutorWeekGrid } from "../TutorWeekGrid"
import { type UITutorHour, type UITutorWeek } from "../../../ports/primary/ui/types/tutorDispoOffres"

export const TUTOR_WEEK_EDITOR_TEXTS = {
    startTitle: "Je suis disponible à partir de…",
    startPlaceholder: "Choisir une date",
    weekTitle: "Je choisis mes créneaux",
    weekHint: "Touche une heure pour la proposer ou la retirer.",
}

/** Les bornes du serveur : d'aujourd'hui à un an. */
function startingDateBounds(): { min: Date; max: Date } {
    const min = new Date()
    const max = new Date(min)
    max.setFullYear(max.getFullYear() + 1)
    return { min, max }
}

export type TutorWeekEditorProps = {
    week: UITutorWeek
    /** Le jour où s'ouvre le calendrier — aujourd'hui quand la date posée est passée. Défaut : la date posée. */
    startingDateOpensOn?: string
    /** Un jour VALIDÉ dans le calendrier — pas chaque jour touché en le parcourant. */
    onPickStartingDate: (date: string) => void
    onPressHour: (hour: UITutorHour) => void
    testID?: string
}

export function TutorWeekEditor(props: TutorWeekEditorProps) {
    const { week, startingDateOpensOn, onPickStartingDate, onPressHour, testID } = props
    // Une fois par montage : la date d'une ouverture, pas un `Date` neuf à chaque rendu.
    const bounds = useMemo(startingDateBounds, [])

    return (
        <Stack gap="6" testID={testID}>
            <Stack gap="3">
                <TutorStepHeading number={1} title={TUTOR_WEEK_EDITOR_TEXTS.startTitle} />
                <DatePicker
                    value={week.startingDate ?? ""}
                    placeholder={TUTOR_WEEK_EDITOR_TEXTS.startPlaceholder}
                    openOn={startingDateOpensOn}
                    onChange={(date) => {
                        if (date !== week.startingDate) onPickStartingDate(date)
                    }}
                    modalLabel={TUTOR_WEEK_EDITOR_TEXTS.startTitle}
                    minDate={bounds.min}
                    maxDate={bounds.max}
                />
            </Stack>
            <Stack gap="4">
                <TutorStepHeading
                    number={2}
                    title={TUTOR_WEEK_EDITOR_TEXTS.weekTitle}
                    hint={TUTOR_WEEK_EDITOR_TEXTS.weekHint}
                />
                <TutorWeekGrid days={week.days} onPressHour={onPressHour} />
            </Stack>
            <TutorWeekSummary days={week.days} />
        </Stack>
    )
}
